import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import CountUp from "react-countup";
import AOS from "aos";
import "aos/dist/aos.css";

import Head from "../Components/Layout/Head";
import FooterNew from "../Components/Layout/FooterNew";
import getHelmetContent from "../Components/Common/HelmetData";

import {
  ArrowRight,
  Brain,
  CheckCircle,
  Cpu,
  FileSearch,
  Layers3,
  ShieldCheck,
  Sparkles,
  Workflow,
  Target,
  Bot,
} from "lucide-react";

const AboutUs = () => {
  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  const helmetContent = getHelmetContent();

  const pageTitle =
    "About ThinkQuotient | AI Automation & Software Solutions Company";

  const metaDescription =
    "ThinkQuotient is an AI automation and software solutions company helping businesses reduce manual work, automate workflows, and build intelligent digital systems.";

  const stats = [
    {
      value: 10,
      suffix: "+",
      label: "Years of Technology Experience",
    },
    {
      value: 100,
      suffix: "+",
      label: "Years of Collective IT Experience",
    },
  ];

  const capabilities = [
    {
      icon: <Brain size={23} />,
      title: "AI Automation",
      desc: "AI-powered systems that reduce repetitive manual work and improve business efficiency.",
    },
    {
      icon: <FileSearch size={23} />,
      title: "Document Intelligence",
      desc: "Extraction, search, summarization, and understanding of PDFs, contracts, invoices, and business documents.",
    },
    {
      icon: <Workflow size={23} />,
      title: "Workflow Automation",
      desc: "Automated workflows across approvals, documents, teams, dashboards, and existing systems.",
    },
    {
      icon: <Cpu size={23} />,
      title: "Custom Software Development",
      desc: "Scalable web, mobile, and enterprise applications tailored to business operations.",
    },
    {
      icon: <ShieldCheck size={23} />,
      title: "Secure AI Implementation",
      desc: "Controlled access, private data handling, and secure deployment models for businesses.",
    },
    {
      icon: <Layers3 size={23} />,
      title: "System Integration",
      desc: "Connecting AI solutions with your current databases, applications, cloud tools, and workflows.",
    },
  ];

  const workflowSteps = [
    {
      title: "Understand",
      desc: "We study your current workflow, documents, users, business rules, and pain points.",
    },
    {
      title: "Design",
      desc: "We identify where AI, automation, document intelligence, or integration can create business value.",
    },
    {
      title: "Build",
      desc: "We develop practical AI and software solutions aligned with real operational needs.",
    },
    {
      title: "Deploy & Improve",
      desc: "We support testing, deployment, feedback, optimization, and future scaling.",
    },
  ];

  const strengths = [
    "Business-first AI implementation, not just technology experiments",
    "Strong software engineering with scalable architecture",
    "Secure handling of business documents and process data",
    "Industry-specific AI solutions for legal, manufacturing, healthcare, and enterprise teams",
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />

        {helmetContent?.meta?.map((metaTag, index) => (
          <meta key={index} name={metaTag.name} content={metaTag.content} />
        ))}

        {helmetContent?.script}

        <script>
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '1317328810324214');
            fbq('track', 'PageView');
          `}
        </script>

        <noscript>{`<img height="1" width="1" style="display:none"
          src="https://www.facebook.com/tr?id=1317328810324214&ev=PageView&noscript=1"
        />`}</noscript>
      </Helmet>

      <div className="overflow-hidden bg-white text-slate-800">
        <Head />

        {/* HERO */}
        <section
          className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-20 sm:py-24 lg:py-28"
          style={{
            backgroundImage: "url('/assets/images/freepik__expand__54122.png')",
          }}
        >
          <div className="absolute inset-0 bg-slate-950/5"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/30 via-blue-950/10 to-transparent"></div>

          <div
            data-aos="zoom-in"
            data-aos-delay="200"
            className="absolute -left-24 top-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl"
          ></div>

          <div
            data-aos="zoom-in"
            data-aos-delay="300"
            className="absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-indigo-500/10 blur-3xl"
          ></div>

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div
                data-aos="fade-up"
                data-aos-delay="100"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 backdrop-blur-md"
              >
                <Sparkles size={16} className="text-blue-300" />
                About ThinkQuotient AI
              </div>

              <h1
                data-aos="fade-up"
                data-aos-delay="200"
                className="mt-6 text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl"
              >
                We Build Practical AI Solutions for Business Automation
              </h1>

              <p
                data-aos="fade-up"
                data-aos-delay="300"
                className="mt-6 max-w-3xl text-base leading-8 text-white/80 sm:text-lg"
              >
                ThinkQuotient helps businesses reduce manual work, automate workflows,
                understand documents, and build intelligent software systems that create
                measurable operational impact.
              </p>

              <div
                data-aos="fade-up"
                data-aos-delay="400"
                className="mt-8 flex flex-col gap-4 sm:flex-row"
              >
                <Link
                  to="/ai-features/law-and-legal"
                  className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 text-sm font-semibold text-slate-950 shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-50"
                >
                  Explore AI Solutions
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHO WE ARE */}
        <section className="relative overflow-hidden py-16 sm:py-20 lg:py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50/90 via-white to-white"></div>

          <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 bottom-20 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div
              data-aos="fade-up"
              data-aos-duration="900"
              className="mx-auto max-w-6xl text-center"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-lg font-semibold text-blue-700 shadow-sm">
                Who We Are
              </div>

              <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
                An AI solutions company built for practical business automation.
              </h2>

              <p className="mx-auto mt-5 max-w-5xl text-base leading-8 text-slate-600 sm:text-lg">
                ThinkQuotient helps businesses reduce manual work, automate workflows,
                understand documents, and build intelligent software systems that deliver
                measurable operational impact.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
              <div data-aos="fade-right" data-aos-delay="150">
                <h3 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  We combine AI thinking, software engineering, and business process understanding.
                </h3>

                <p className="mt-5 text-base leading-8 text-slate-600">
                  The name ThinkQuotient represents analytical thinking, logical reasoning,
                  and problem-solving. We apply that mindset to design AI and software
                  systems that simplify complex business operations.
                </p>

                <p className="mt-4 text-base leading-8 text-slate-600">
                  Our work focuses on AI automation, document intelligence, workflow
                  automation, custom software platforms, and system integration for
                  businesses that want practical technology outcomes.
                </p>

                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    "AI-powered automation",
                    "Document intelligence",
                    "Custom software platforms",
                    "Workflow integration",
                  ].map((item, index) => (
                    <div
                      key={index}
                      data-aos="fade-up"
                      data-aos-delay={250 + index * 80}
                      className="group flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-lg"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                        <CheckCircle size={17} />
                      </span>

                      <span className="text-sm font-semibold text-slate-700">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div data-aos="fade-left" data-aos-delay="250">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 blur-xl"></div>

                  <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.10)]">
                    <div className="relative overflow-hidden bg-slate-950 p-7 text-white">
                      <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-blue-500 via-cyan-400 to-indigo-500"></div>

                      <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px]"></div>

                      <div className="relative z-10">
                        <div
                          data-aos="zoom-in"
                          data-aos-delay="450"
                          className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-blue-300"
                        >
                          <Bot size={24} strokeWidth={2.1} />
                        </div>

                        <h3
                          data-aos="fade-up"
                          data-aos-delay="520"
                          className="mt-5 text-2xl font-bold tracking-tight text-white"
                        >
                          Our Focus
                        </h3>

                        <p
                          data-aos="fade-up"
                          data-aos-delay="590"
                          className="mt-3 text-sm leading-7 text-white/70"
                        >
                          Creating practical AI solutions that reduce manual work, improve accuracy,
                          and support secure, scalable business operations.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
                      {stats.map((item, index) => (
                        <div
                          key={index}
                          data-aos="zoom-in"
                          data-aos-delay={400 + index * 120}
                          className="rounded-2xl border border-slate-100 bg-slate-50 p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-md"
                        >
                          <h3 className="text-3xl font-bold text-slate-950">
                            <CountUp end={item.value} duration={3} />
                            {item.suffix}
                          </h3>

                          <p className="mt-2 text-xs font-semibold leading-relaxed text-slate-500">
                            {item.label}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div
                      data-aos="fade-up"
                      data-aos-delay="550"
                      className="border-t border-slate-100 bg-white px-5 py-4"
                    >
                      <p className="text-sm leading-7 text-slate-600">
                        We help businesses move from manual, disconnected operations to
                        intelligent, automated, and data-driven workflows.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT WE DO */}
        <section className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-24">
          <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 bottom-24 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div
                data-aos="fade-up"
                data-aos-delay="100"
                className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm"
              >
                <Cpu size={16} />
                What We Do
              </div>

              <h2
                data-aos="fade-up"
                data-aos-delay="200"
                className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl"
              >
                AI solutions designed for day-to-day business operations
              </h2>

              <p
                data-aos="fade-up"
                data-aos-delay="300"
                className="mt-4 text-base leading-7 text-slate-600"
              >
                We help companies convert manual, disconnected, and document-heavy
                processes into intelligent workflows that can search, extract, summarize,
                automate, assist, and scale.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {capabilities.map((item, index) => (
                <div
                  key={index}
                  data-aos="fade-up"
                  data-aos-delay={index * 80}
                  className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                >
                  <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-indigo-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-100 bg-slate-50 text-slate-700 transition-all duration-300 group-hover:border-blue-100 group-hover:bg-blue-50 group-hover:text-blue-600">
                      {item.icon}
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-slate-950">
                        {item.title}
                      </h3>

                      <p className="mt-2 text-sm leading-7 text-slate-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW WE WORK */}
        <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-24">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
          <div className="absolute -left-24 bottom-16 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 top-16 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
              <div data-aos="fade-right" data-aos-delay="100">
                <div
                  data-aos="fade-up"
                  data-aos-delay="150"
                  className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm"
                >
                  <Workflow size={16} />
                  How We Work
                </div>

                <h2
                  data-aos="fade-up"
                  data-aos-delay="250"
                  className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl"
                >
                  We start with the business workflow, not with the technology.
                </h2>

                <p
                  data-aos="fade-up"
                  data-aos-delay="350"
                  className="mt-5 text-base leading-8 text-slate-600"
                >
                  Our process is practical and implementation-focused. We first understand
                  your documents, users, business rules, existing systems, and bottlenecks.
                  Then we design the AI automation layer that can create the highest impact.
                </p>

                <div
                  data-aos="fade-up"
                  data-aos-delay="450"
                  className="mt-8 rounded-3xl border border-slate-200 bg-slate-50 p-5"
                >
                  <p className="text-sm font-semibold text-slate-950">
                    Result: AI systems that are easier to adopt, easier to maintain, and
                    aligned with actual business operations.
                  </p>
                </div>
              </div>

              <div data-aos="fade-left" data-aos-delay="150" className="relative">
                <div className="absolute left-5 top-6 hidden h-[calc(100%-3rem)] w-px bg-gradient-to-b from-blue-500 via-indigo-300 to-transparent sm:block"></div>

                <div className="space-y-4">
                  {workflowSteps.map((item, index) => (
                    <div
                      key={index}
                      data-aos="fade-up"
                      data-aos-delay={250 + index * 90}
                      className="group relative rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_20px_60px_rgba(15,23,42,0.10)]"
                    >
                      <div className="flex gap-4">
                        <div className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition-all duration-300 group-hover:bg-slate-950">
                          {index + 1}
                        </div>

                        <div>
                          <h3 className="font-bold text-slate-950">
                            {item.title}
                          </h3>

                          <p className="mt-2 text-sm leading-7 text-slate-600">
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY + CTA */}
        <section className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-24">
          <div className="absolute -left-24 top-24 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 bottom-24 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
            <div data-aos="fade-right" data-aos-delay="100">
              <div
                data-aos="fade-up"
                data-aos-delay="180"
                className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm"
              >
                <ShieldCheck size={16} />
                Why ThinkQuotient
              </div>

              <h2
                data-aos="fade-up"
                data-aos-delay="260"
                className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl"
              >
                AI solutions that are practical, secure, and built for real business use.
              </h2>

              <p
                data-aos="fade-up"
                data-aos-delay="340"
                className="mt-5 text-base leading-8 text-slate-600"
              >
                We combine software engineering, business process understanding, and AI-first
                execution to help organizations move from manual operations to intelligent,
                scalable workflows.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-4">
                {strengths.map((item, index) => (
                  <div
                    key={index}
                    data-aos="fade-up"
                    data-aos-delay={420 + index * 80}
                    className="group flex gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-lg"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                      <CheckCircle size={18} />
                    </div>

                    <p className="text-sm font-medium leading-7 text-slate-600">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div data-aos="fade-left" data-aos-delay="150">
              <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_24px_80px_rgba(15,23,42,0.10)] sm:p-10">
                <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400"></div>

                <div
                  data-aos="zoom-in"
                  data-aos-delay="250"
                  className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-blue-100/80 blur-3xl"
                ></div>

                <div
                  data-aos="zoom-in"
                  data-aos-delay="350"
                  className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-indigo-100/80 blur-3xl"
                ></div>

                <div className="relative z-10">
                  <div
                    data-aos="zoom-in"
                    data-aos-delay="250"
                    className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 text-blue-600"
                  >
                    <Sparkles size={22} />
                  </div>

                  <h3
                    data-aos="fade-up"
                    data-aos-delay="320"
                    className="mt-6 text-3xl font-bold tracking-tight text-slate-950"
                  >
                    Ready to automate your business with AI?
                  </h3>

                  <p
                    data-aos="fade-up"
                    data-aos-delay="390"
                    className="mt-4 text-base leading-8 text-slate-600"
                  >
                    Let’s identify the right AI opportunities for your business and design
                    a solution that delivers measurable operational impact.
                  </p>

                  <div className="mt-7 space-y-3">
                    {[
                      "Workflow automation opportunities",
                      "Document intelligence use cases",
                      "AI chatbot and assistant possibilities",
                    ].map((item, index) => (
                      <div
                        key={index}
                        data-aos="fade-up"
                        data-aos-delay={460 + index * 80}
                        className="flex items-center gap-3"
                      >
                        <span className="h-2 w-2 rounded-full bg-blue-600"></span>
                        <span className="text-sm font-medium text-slate-700">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/"
                    data-aos="fade-up"
                    data-aos-delay="720"
                    className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-[0_18px_40px_rgba(37,99,235,0.25)] sm:w-auto"
                  >
                    Start a Conversation
                    <ArrowRight
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FooterNew />
      </div>
    </>
  );
};

export default AboutUs;